import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Users, Plus, Calendar as CalendarIcon, Car, ArrowRight, MapPin, Clock,
  HandHelping, Copy, Share2, Check, Sparkles, User as UserIcon
} from "lucide-react";
import { pickColor } from "@/lib/palette";
import { modeOf } from "@/components/ModePicker";
import ActivitiesCalendar from "@/components/ActivitiesCalendar";

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

function dayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "OGGI";
  if (diff === 1) return "DOMANI";
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()].slice(0,3).toUpperCase()}`;
}

function StatCard({ icon: Icon, value, label, to, color, onClick }) {
  const className = "brut-card brut-card-hover p-4 flex flex-col items-center text-center group transition-all";
  const inner = (
    <>
      <div className="w-11 h-11 grid place-items-center rounded-2xl mb-2 group-hover:scale-110 transition-transform" style={{ backgroundColor: `${color}1F`, color }}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <p className="font-heading text-2xl font-black text-[#1A1D29] leading-none">{value}</p>
      <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-1.5">{label}</p>
    </>
  );
  const tid = `home-stat-${label.toLowerCase().replace(/\s/g, "-")}`;
  if (onClick) {
    return <button type="button" onClick={onClick} className={`${className} w-full`} data-testid={tid}>{inner}</button>;
  }
  return <Link to={to} className={className} data-testid={tid}>{inner}</Link>;
}

function RideMiniCard({ ride, available = false, showGroup = false, mineHighlight = false }) {
  const m = modeOf(ride.mode);
  const ModeIcon = m.icon;
  const taken = (ride.passengers || []).length;
  const free = Math.max(0, (ride.seats || 0) - taken);
  const otherName = ride.type === "request" ? (ride.requester_name || "?") : (ride.driver_name || "?");
  const c = pickColor(otherName);
  const cardBaseClass = "block shrink-0 w-80 rounded-3xl p-4 transition-all border";
  const cardClass = mineHighlight
    ? `${cardBaseClass} bg-gradient-to-br from-[#FFF6E0] to-[#FFE9C2] border-[#FF9E1B]/40 shadow-[0_6px_18px_rgba(255,158,27,0.18)] hover:shadow-[0_10px_24px_rgba(255,158,27,0.26)]`
    : `${cardBaseClass} bg-white border-[#ECEEF5] shadow-[0_4px_14px_rgba(20,32,80,0.06)] hover:border-[#1A2A6C]/30 hover:shadow-[0_8px_22px_rgba(20,32,80,0.10)]`;
  return (
    <Link
      to={`/rides/${ride.id}`}
      className={cardClass}
      data-testid={`home-ride-card-${ride.id}`}
      data-mine={mineHighlight ? "true" : undefined}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="brut-chip" style={{ backgroundColor: `${m.color}1F`, color: m.color }}>
          <ModeIcon className="w-3 h-3" /> {m.label}
        </span>
        {mineHighlight ? (
          <span className="brut-chip bg-[#FF9E1B] text-white">La tua richiesta</span>
        ) : available ? (
          <span className="text-sm font-semibold text-[#8A91A8]"><span className="text-[#FF4D5E] font-bold">{free}</span> posti liberi</span>
        ) : (
          <span className="brut-chip bg-[#F5F6FA] text-[#1A2A6C]">{dayLabel(ride.date)} · {ride.time}</span>
        )}
      </div>

      {/* Route visual */}
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2 h-2 rounded-full bg-[#26B5A8] shrink-0" />
        <span className="text-xs text-[#26B5A8] font-bold truncate">{ride.origin || "—"}</span>
        <span className="flex-1 h-px bg-[#ECEEF5]" />
        <span className="w-2 h-2 rounded-full bg-[#FF4D5E] shrink-0" />
      </div>
      <p className="text-right font-heading text-base font-bold text-[#FF4D5E] -mt-2 mb-3 truncate">{ride.destination}</p>

      <div className="flex items-center justify-between pt-3 border-t border-[#F5F6FA]">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 grid place-items-center rounded-full font-heading font-bold text-sm shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
            {otherName.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#1A1D29] truncate">{otherName}</p>
            {showGroup && ride.group_name && (
              <p className="text-[11px] text-[#8A91A8] truncate">{ride.group_name}</p>
            )}
          </div>
        </div>
        <div className="text-right shrink-0 ml-2">
          {available ? (
            <span className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-[#26B5A8] text-white text-sm font-bold shadow-[0_4px_12px_rgba(38,181,168,0.30)]">
              Apri <ArrowRight className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span className="brut-chip bg-[#F5F6FA] text-[#1A2A6C]">{ride.time}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function Carousel({ children, testId }) {
  const ref = useRef(null);
  return (
    <div ref={ref} className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory" style={{ scrollbarWidth: "none" }} data-testid={testId}>
      {children}
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [home, setHome] = useState({ stats: { groups: 0, activities: 0, rides: 0, available: 0 }, my_upcoming_rides: [], available_rides: [], next_ride: null });
  const [groups, setGroups] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showMyRides, setShowMyRides] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, g] = await Promise.all([
        api.get("/home"),
        api.get("/groups"),
      ]);
      setHome(h.data);
      setGroups(g.data.groups || []);
      setActiveId(g.data.active_group_id || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-open sections based on ?view= param (used by bottom nav)
  useEffect(() => {
    const view = searchParams.get("view");
    if (view === "groups") {
      setShowGroups(true);
      setTimeout(() => document.getElementById("groups-detail")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } else if (view === "calendar") {
      setShowCalendar(true);
      setTimeout(() => document.querySelector('[data-testid="home-calendar-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [searchParams]);

  const switchGroup = async (gid) => {
    try {
      await api.post(`/groups/switch/${gid}`);
      await refresh();
      setActiveId(gid);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(`Codice ${code} copiato`);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Impossibile copiare il codice");
    }
  };

  const shareGroup = async (g) => {
    const url = `${window.location.origin}/?invite=${g.invite_code}`;
    const text = `Unisciti al gruppo "${g.name}" su Hopzigo!`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Hopzigo", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link invito copiato");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non disponibile");
    }
  };

  const firstName = user?.name?.split(" ")[0] || "";
  const upcomingRides = (home.my_upcoming_rides || []).slice(0, 2);
  const todayCount = (home.my_upcoming_rides || []).filter((r) => r.date === new Date().toISOString().slice(0, 10)).length;

  const roleOf = (r) => {
    const isDriver = r.driver_id === user?.id;
    const isPassenger = (r.passengers || []).some((p) => p.user_id === user?.id);
    if (isDriver) return { key: "driver", bg: "linear-gradient(135deg, #1A2A6C 0%, #14205A 100%)", label: t("home.roles.driver"), Icon: Car };
    if (isPassenger) return { key: "passenger", bg: "linear-gradient(135deg, #26B5A8 0%, #1A8F84 100%)", label: t("home.roles.passenger"), Icon: UserIcon };
    return { key: "requester", bg: "linear-gradient(135deg, #FF9E1B 0%, #E07A00 100%)", label: t("home.roles.requester"), Icon: MapPin };
  };

  return (
    <div className="anim-slide-up space-y-6" data-testid="home-page">
      {/* Hero greeting card */}
      <div className="relative overflow-hidden rounded-3xl bg-[#0B1437] text-white p-5 sm:p-6 shadow-[0_18px_40px_rgba(11,20,55,0.30)]" data-testid="home-hero">
        <div aria-hidden="true" className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#26B5A8]/30 blur-2xl pointer-events-none" />
        <div aria-hidden="true" className="absolute -right-6 bottom-0 w-40 h-40 rounded-full bg-[#7E3AED]/30 blur-2xl pointer-events-none" />
        <div className="relative">
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
            {t("home.hello")} {firstName}!
          </h1>
          {todayCount > 0 ? (
            <p className="text-white/90 mt-2 text-sm">
              {t(todayCount === 1 ? "home.one_ride_today" : "home.many_rides_today", { count: todayCount })}
            </p>
          ) : (
            <p className="text-white/80 mt-2 text-sm">{t("home.no_rides_today")}</p>
          )}
        </div>
      </div>

      {/* Inline calendar (toggled) */}
      {showCalendar && (
        <div className="anim-slide-up" data-testid="home-calendar-section">
          <ActivitiesCalendar />
        </div>
      )}

      {/* Detailed Groups section (toggled) */}
      {showGroups && (
        <section id="groups-detail" className="anim-slide-up" data-testid="home-groups-detail">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1A2A6C]" /> Gruppi attivi
            </h2>
            <span className="text-sm font-bold text-[#26B5A8]">
              {groups.length} {groups.length === 1 ? "gruppo" : "gruppi"}
            </span>
          </div>
          {groups.length === 0 ? (
            <div className="brut-card p-6 text-center">
              <p className="text-[#8A91A8] mb-3">Non sei ancora in nessun gruppo.</p>
              <Link to="/onboarding" className="brut-btn inline-flex">
                <Plus className="w-4 h-4" /> Entra o crea un gruppo
              </Link>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="home-groups-detail-list">
              {groups.map((g) => {
                const c = pickColor(g.id);
                return (
                  <li key={g.id} className="brut-card p-4 sm:p-5" data-testid={`home-group-detail-${g.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 grid place-items-center rounded-2xl font-heading font-black text-xl shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                        {g.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-bold text-lg text-[#1A1D29] leading-tight truncate">{g.name}</p>
                        <p className="text-xs text-[#8A91A8] mt-0.5">
                          Codice invito · <span className="font-mono font-bold text-[#1A2A6C]">{g.invite_code}</span>
                        </p>
                      </div>
                      <span className={`brut-chip ${g.is_owner ? "bg-[#EFE8FD] text-[#7E3AED]" : "bg-[#D4F1ED] text-[#26B5A8]"}`}>
                        {g.is_owner ? "Admin" : "Membro"}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#ECEEF5] text-center">
                      <div data-testid={`home-group-stat-families-${g.id}`}>
                        <p className="font-heading text-2xl font-black text-[#1A2A6C]">{g.members_count}</p>
                        <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-0.5">Famiglie</p>
                      </div>
                      <div data-testid={`home-group-stat-kids-${g.id}`}>
                        <p className="font-heading text-2xl font-black text-[#FF9E1B]">{g.kids_count}</p>
                        <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-0.5">Bambini</p>
                      </div>
                      <div data-testid={`home-group-stat-rides-${g.id}`}>
                        <p className="font-heading text-2xl font-black text-[#FF4D5E]">{g.rides_count}</p>
                        <p className="text-[11px] uppercase tracking-widest font-semibold text-[#8A91A8] mt-0.5">Corse</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
                      <div className="min-w-0">
                        {g.next_ride ? (
                          <p className="text-sm text-[#5A6178]">
                            Prossima corsa: <span className="font-bold text-[#1A1D29]">{dayLabel(g.next_ride.date)} {g.next_ride.time}</span>
                            <span className="block text-xs text-[#8A91A8] truncate">→ {g.next_ride.destination}</span>
                          </p>
                        ) : (
                          <p className="text-sm text-[#8A91A8]">Nessuna corsa programmata</p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          if (g.id !== activeId) await switchGroup(g.id);
                          nav("/dashboard");
                        }}
                        className="shrink-0 inline-flex items-center gap-1 px-4 py-2 rounded-2xl bg-[#E5E9F5] text-[#1A2A6C] font-bold text-sm hover:bg-[#D5DAF0]"
                        data-testid={`home-group-manage-${g.id}`}
                      >
                        {g.is_owner ? "Gestisci" : "Apri"} <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* My rides detail (toggled) */}
      {showMyRides && (
        <section id="my-rides-detail" className="anim-slide-up" data-testid="home-myrides-detail">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <Car className="w-5 h-5 text-[#1A2A6C]" /> Le mie corse
            </h2>
            <Link to="/dashboard" className="text-sm font-bold text-[#26B5A8] inline-flex items-center gap-1 hover:underline" data-testid="home-myrides-all">
              Vai al calendario <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {(home.my_upcoming_rides || []).length === 0 ? (
            <div className="brut-card p-6 text-center">
              <p className="text-[#8A91A8] mb-3">Non hai corse programmate o prenotate.</p>
              <Link to="/rides/new" className="brut-btn inline-flex">
                <Plus className="w-4 h-4" /> Crea una corsa
              </Link>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="home-myrides-list">
              {home.my_upcoming_rides.map((r) => {
                const m = modeOf(r.mode);
                const ModeIcon = m.icon;
                const taken = (r.passengers || []).length;
                const free = Math.max(0, (r.seats || 0) - taken);
                const isDriver = r.driver_id === user?.id;
                const isRequester = r.requester_id === user?.id;
                const otherName = r.type === "request" ? (r.requester_name || "?") : (r.driver_name || "?");
                const c = pickColor(otherName);
                let myRole = "Passeggero";
                if (isDriver) myRole = "Guidatore";
                else if (isRequester) myRole = "Richiedente";
                return (
                  <li key={r.id} className="brut-card p-4" data-testid={`home-myride-${r.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="brut-chip" style={{ backgroundColor: `${m.color}1F`, color: m.color }}>
                        <ModeIcon className="w-3 h-3" /> {m.label}
                      </span>
                      <span className="brut-chip bg-[#F5F6FA] text-[#1A2A6C]">{dayLabel(r.date)} · {r.time}</span>
                    </div>
                    {r.group_name && (
                      <p className="text-[11px] uppercase tracking-widest font-bold text-[#8A91A8] mb-2">📍 {r.group_name}</p>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full bg-[#26B5A8] shrink-0" />
                      <span className="text-sm font-bold text-[#26B5A8] truncate">{r.origin || "—"}</span>
                      <span className="flex-1 h-px bg-[#ECEEF5]" />
                      <span className="w-2 h-2 rounded-full bg-[#FF4D5E] shrink-0" />
                    </div>
                    <p className="text-right font-heading text-base font-bold text-[#FF4D5E] mb-3 truncate">{r.destination}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-[#F5F6FA]">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 grid place-items-center rounded-full font-heading font-bold text-sm shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                          {otherName.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-[#1A1D29] truncate">{isDriver || isRequester ? "Tu" : otherName}</p>
                          <p className="text-[11px] text-[#8A91A8]">{myRole}{!isDriver && !isRequester ? ` di ${otherName}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isDriver && (
                          <span className="text-xs text-[#8A91A8]"><span className="text-[#FF4D5E] font-bold">{free}</span> liberi</span>
                        )}
                        <Link to={`/rides/${r.id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-[#1A2A6C] text-white text-sm font-bold hover:bg-[#14205A]" data-testid={`home-myride-open-${r.id}`}>
                          Apri <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* Upcoming rides — first full, second compact */}
      {upcomingRides.length > 0 && (
        <section data-testid="home-next-trip">
          <div className="flex items-end justify-between mb-2.5">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#1A2A6C]" /> {t("home.upcomingTitle")}
            </h2>
            <Link to="/rides?filter=mine" className="text-sm font-bold text-[#1A2A6C] inline-flex items-center gap-1 hover:underline" data-testid="home-next-trip-all">
              {t("common.seeAll")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="space-y-2.5">
            {upcomingRides.map((r, idx) => {
              const role = roleOf(r);
              const RoleIcon = role.Icon;
              const paxCount = (r.passengers || []).length;
              const isPrimary = idx === 0;
              return (
                <Link
                  key={r.id}
                  to={`/rides/${r.id}`}
                  className={`relative block overflow-hidden rounded-2xl text-white transition-all hover:translate-y-[-1px] ${isPrimary ? "px-4 py-3" : "px-4 py-2.5"}`}
                  style={{ background: role.bg, boxShadow: isPrimary ? "0 10px 24px rgba(0,0,0,0.18)" : "0 6px 16px rgba(0,0,0,0.14)" }}
                  data-testid={`home-next-trip-card-${idx}`}
                  data-role={role.key}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm font-bold uppercase tracking-widest ${isPrimary ? "text-[10px]" : "text-[9px]"}`}>
                          <RoleIcon className="w-3 h-3" /> {role.label}
                        </span>
                        <span className={`font-bold uppercase tracking-widest text-white/85 ${isPrimary ? "text-[11px]" : "text-[10px]"}`}>
                          {dayLabel(r.date)} · {r.time}
                        </span>
                      </div>
                      {isPrimary ? (
                        <>
                          <h3 className="font-heading text-lg sm:text-xl font-black leading-tight truncate">
                            {r.destination}
                          </h3>
                          {r.origin && (
                            <p className="text-white/85 text-xs mt-0.5 truncate">
                              {r.origin} → {r.destination}
                            </p>
                          )}
                          <p className="text-[11px] font-semibold text-white/85 mt-1.5">
                            {paxCount === 0 ? t("home.noPassengers") : t(paxCount === 1 ? "home.one_child_onboard" : "home.many_children_onboard", { count: paxCount })}
                          </p>
                        </>
                      ) : (
                        <p className="font-heading text-sm font-bold leading-tight truncate">
                          {r.origin ? `${r.origin} → ${r.destination}` : r.destination}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0">
                      <ArrowRight className={`text-white/80 ${isPrimary ? "w-5 h-5" : "w-4 h-4"}`} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Available rides carousel - always shown */}
      <section data-testid="home-available">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#1A2A6C]" /> {t("home.availableTitle")}
          </h2>
          <Link to="/rides?filter=available" className="text-sm font-bold text-[#26B5A8] inline-flex items-center gap-1 hover:underline" data-testid="home-available-all">
            {t("common.seeAll")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {(home.available_rides || []).length === 0 ? (
          <div className="brut-card p-5 text-center" data-testid="home-available-empty">
            <p className="text-sm text-[#8A91A8]">{t("home.noAvailable")}</p>
            <Link to="/rides/new?type=request" className="text-sm font-bold text-[#FF9E1B] inline-flex items-center gap-1 hover:underline mt-2" data-testid="home-available-empty-cta">
              <Plus className="w-3.5 h-3.5" /> {t("home.requestARide")}
            </Link>
          </div>
        ) : (
          <Carousel testId="home-available-carousel">
            {home.available_rides.map((r) => (
              <div key={r.id} className="snap-start">
                <RideMiniCard ride={r} available showGroup />
              </div>
            ))}
          </Carousel>
        )}
      </section>

      {/* Ride requests carousel - always shown */}
      <section data-testid="home-requests">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <HandHelping className="w-5 h-5 text-[#FF9E1B]" /> {t("home.requestsTitle")}
          </h2>
          <Link to="/rides?filter=requests" className="text-sm font-bold text-[#FF9E1B] inline-flex items-center gap-1 hover:underline" data-testid="home-requests-all">
            {t("common.seeAll")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {(home.ride_requests || []).length === 0 ? (
          <div className="brut-card p-5 text-center" data-testid="home-requests-empty">
            <p className="text-sm text-[#8A91A8]">{t("home.noRequests")}</p>
            <Link to="/rides/new?type=request" className="text-sm font-bold text-[#FF9E1B] inline-flex items-center gap-1 hover:underline mt-2" data-testid="home-requests-empty-cta">
              <Plus className="w-3.5 h-3.5" /> {t("home.requestARide")}
            </Link>
          </div>
        ) : (
          <Carousel testId="home-requests-carousel">
            {home.ride_requests.map((r) => (
              <div key={r.id} className="snap-start">
                <RideMiniCard ride={r} showGroup mineHighlight={r.requester_id === user?.id} />
              </div>
            ))}
          </Carousel>
        )}
      </section>

      {/* Invite banner */}
      {groups.length > 0 && (
        <button
          onClick={() => shareGroup(groups[0])}
          className="w-full rounded-3xl bg-[#FFF6E0] border border-[#FFE9C2] p-4 flex items-center gap-4 text-left hover:bg-[#FFF0D2] transition-colors"
          data-testid="home-invite-banner"
        >
          <div className="w-12 h-12 grid place-items-center rounded-2xl bg-[#FF9E1B] text-white shrink-0">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-[#1A1D29]">Invita altri genitori</p>
            <p className="text-xs text-[#B07512]">Più siete, meno guidate!</p>
          </div>
          <span className="px-4 py-2 rounded-xl bg-[#FF9E1B] text-white font-bold text-sm shrink-0">Condividi</span>
        </button>
      )}

      {/* My groups */}
      <section id="groups" className="scroll-mt-20" data-testid="home-groups">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-[#1A2A6C]" /> I miei gruppi
          </h2>
          <Link to="/onboarding" className="text-sm font-bold text-[#26B5A8] inline-flex items-center gap-1 hover:underline" data-testid="home-groups-add">
            <Plus className="w-3.5 h-3.5" /> Nuovo
          </Link>
        </div>
        {groups.length === 0 ? (
          <div className="brut-card p-6 text-center">
            <p className="text-[#8A91A8] mb-3">Non sei ancora in nessun gruppo.</p>
            <Link to="/onboarding" className="brut-btn inline-flex">
              <Plus className="w-4 h-4" /> Entra o crea un gruppo
            </Link>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="home-groups-list">
            {groups.map((g) => {
              const isActive = g.id === activeId;
              const c = pickColor(g.id);
              return (
                <li key={g.id} className="brut-card p-3 flex items-center gap-3" data-testid={`home-group-${g.id}`}>
                  <div className="w-12 h-12 grid place-items-center rounded-2xl font-heading font-black text-lg shrink-0" style={{ backgroundColor: c.bg, color: c.text }}>
                    {g.name.charAt(0).toUpperCase()}
                  </div>
                  <button
                    onClick={async () => {
                      if (!isActive) await switchGroup(g.id);
                      nav("/dashboard");
                    }}
                    className="flex-1 text-left min-w-0"
                    data-testid={`home-group-switch-${g.id}`}
                  >
                    <p className="font-bold text-[#1A1D29] truncate">{g.name}</p>
                    <p className="text-xs text-[#8A91A8] truncate">
                      {g.members_count} {g.members_count === 1 ? "membro" : "membri"} · Codice {g.invite_code}
                    </p>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => copyCode(g.invite_code)} className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]" title="Copia codice" data-testid={`home-copy-${g.id}`}>
                      {copied === g.invite_code ? <Check className="w-4 h-4 text-[#26B5A8]" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button onClick={() => shareGroup(g)} className="w-9 h-9 grid place-items-center rounded-xl bg-[#F5F6FA] text-[#1A2A6C] hover:bg-[#E5E9F5]" title="Condividi" data-testid={`home-share-${g.id}`}>
                      <Share2 className="w-4 h-4" />
                    </button>
                    {isActive ? (
                      <Link to="/dashboard" className="brut-chip bg-[#D4F1ED] text-[#26B5A8] ml-1" data-testid={`home-active-open-${g.id}`}>Attivo</Link>
                    ) : (
                      <button onClick={async () => { await switchGroup(g.id); nav("/dashboard"); }} className="text-xs font-bold text-[#26B5A8] hover:underline px-2" data-testid={`home-open-${g.id}`}>Apri</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {loading && groups.length === 0 && (
        <p className="text-center text-[#8A91A8] py-4">Caricamento…</p>
      )}
    </div>
  );
}
